"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PREVIEW_SOURCE_MARKS_MAX = exports.PREVIEW_CONTENT_TEXT_MAX = exports.canonicalPreviewRoutePath = exports.AUTHOR_PREVIEW_FLOW_RUN_SEGMENT = exports.AUTHOR_PREVIEW_ACCESS_SEGMENT = void 0;
exports.createCanonicalPreviewRoutePath = createCanonicalPreviewRoutePath;
exports.createPreviewSelectionCodec = createPreviewSelectionCodec;
exports.parsePreviewSelectionMessage = parsePreviewSelectionMessage;
exports.parsePreviewPresenceMessage = parsePreviewPresenceMessage;
exports.parsePreviewContentPickMessage = parsePreviewContentPickMessage;
exports.parsePreviewContentEditCommand = parsePreviewContentEditCommand;
exports.parsePreviewContentInputMessage = parsePreviewContentInputMessage;
exports.parsePreviewContentPlacesMessage = parsePreviewContentPlacesMessage;
exports.parsePreviewPointMessage = parsePreviewPointMessage;
exports.parsePreviewPointersMessage = parsePreviewPointersMessage;
exports.createPreviewSelectionUrl = createPreviewSelectionUrl;
exports.readPreviewSelectionBootstrap = readPreviewSelectionBootstrap;
exports.installAppBasePreviewSelectionRuntime = installAppBasePreviewSelectionRuntime;
/** Transport segments shared by the server mount, relay and selection codec. */
exports.AUTHOR_PREVIEW_ACCESS_SEGMENT = '_access';
exports.AUTHOR_PREVIEW_FLOW_RUN_SEGMENT = '_flow';
/** The App route after its optional credential and Flow-run transport segments. */
function createCanonicalPreviewRoutePath(accessSegment, flowSegment) {
    return function canonicalPreviewRoutePath(suffix) {
        const parts = suffix.split('/');
        let offset = 1;
        const signedToken = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
        if (parts[offset] === accessSegment &&
            signedToken.test(parts[offset + 1] ?? ''))
            offset += 2;
        if (parts[offset] === flowSegment &&
            signedToken.test(parts[offset + 1] ?? ''))
            offset += 2;
        return '/' + parts.slice(offset).join('/');
    };
}
exports.canonicalPreviewRoutePath = createCanonicalPreviewRoutePath(exports.AUTHOR_PREVIEW_ACCESS_SEGMENT, exports.AUTHOR_PREVIEW_FLOW_RUN_SEGMENT);
/** As many characters as a typed cell value may have; a message carries no more. */
exports.PREVIEW_CONTENT_TEXT_MAX = 4000;
/** At most this many tables of one page are framed. */
exports.PREVIEW_SOURCE_MARKS_MAX = 32;
/** Self-contained so the delivered iframe executes exactly the shared codec. */
function createPreviewSelectionCodec(routePath) {
    // Equal to `PREVIEW_CONTENT_TEXT_MAX`; named again because this function
    // is also delivered into the frame as its source text.
    const CONTENT_TEXT_MAX = 4000;
    const record = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
    const keys = (value, expected) => Object.keys(value).length === expected.length &&
        expected.every(key => Object.prototype.hasOwnProperty.call(value, key));
    const bounded = (value, max, nonempty = false) => typeof value === 'string' &&
        (!nonempty || value.length > 0) &&
        value.length <= max * 2 &&
        Array.from(value).length <= max &&
        !/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/u.test(value);
    // A picture address the page may load; anything else shows initials.
    const validAvatarUrl = (value) => value === null ||
        (typeof value === 'string' &&
            value.length <= 2048 &&
            /^https?:\/\/[^\s"'<>\\]+$/u.test(value));
    const positive = (value) => typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
    const validScope = (value) => {
        if (!record(value) ||
            !keys(value, [
                'workspaceId',
                'viewId',
                'appBaseId',
                'basePath',
                'routePath',
                'artifactVersion',
                'frameId',
            ]))
            return false;
        if (!['workspaceId', 'viewId', 'appBaseId', 'frameId'].every(key => bounded(value[key], 256, true)) ||
            !positive(value.artifactVersion))
            return false;
        if (!bounded(value.basePath, 2048, true) ||
            !bounded(value.routePath, 2048, true) ||
            !value.routePath.startsWith('/') ||
            /[\\?#]/u.test(value.routePath))
            return false;
        const prefix = `/api/workspaces/${encodeURIComponent(String(value.workspaceId))}/app-bases/${encodeURIComponent(String(value.appBaseId))}/preview/${value.artifactVersion}/`;
        if (!value.basePath.startsWith(prefix))
            return false;
        const slug = value.basePath.slice(prefix.length);
        return Boolean(slug &&
            !/[\/\\?#]/u.test(slug) &&
            !/%(?:2e|2f|5c)/iu.test(slug) &&
            slug !== '.' &&
            slug !== '..');
    };
    const matchesScope = (left, right) => Object.keys(left).every(key => left[key] === right[key]);
    const validOrigin = (value) => {
        if (typeof value !== 'string')
            return false;
        try {
            const url = new URL(value);
            return (['http:', 'https:'].includes(url.protocol) &&
                !url.username &&
                !url.password &&
                url.origin === value);
        }
        catch {
            return false;
        }
    };
    /** The frame shared by every sequenced message: exact fields, type, scope. */
    const envelopeIssue = (data, expected, type, payloadKey, optionalKey) => {
        if (!record(data) ||
            !keys(data, [
                'schemaVersion',
                'type',
                'scope',
                'sequence',
                payloadKey,
                ...(optionalKey !== undefined && optionalKey in data
                    ? [optionalKey]
                    : []),
            ]) ||
            data.schemaVersion !== 1 ||
            data.type !== type ||
            !positive(data.sequence) ||
            !validScope(data.scope) ||
            !validScope(expected))
            return 'invalid';
        return matchesScope(data.scope, expected) ? null : 'stale';
    };
    const sizeIssue = (data) => {
        try {
            return new TextEncoder().encode(JSON.stringify(data)).byteLength > 16384
                ? 'oversized'
                : null;
        }
        catch {
            return 'invalid';
        }
    };
    const validColumnId = (value) => value === null || bounded(value, 256, true);
    const parseMessage = (data, expected) => {
        const envelope = envelopeIssue(data, expected, 'streamlain:preview-selection', 'selection');
        if (envelope)
            return { ok: false, reason: envelope };
        const selection = data.selection;
        if (selection !== null) {
            if (!record(selection) ||
                !keys(selection, [
                    'elementId',
                    'tag',
                    'role',
                    'label',
                    'textExcerpt',
                    'neighbours',
                ]) ||
                !bounded(selection.elementId, 256, true) ||
                typeof selection.tag !== 'string' ||
                !(selection.role === null || typeof selection.role === 'string') ||
                typeof selection.label !== 'string' ||
                typeof selection.textExcerpt !== 'string' ||
                !Array.isArray(selection.neighbours) ||
                !selection.neighbours.every(value => typeof value === 'string'))
                return { ok: false, reason: 'invalid' };
            if (!bounded(selection.tag, 32, true) ||
                !(selection.role === null || bounded(selection.role, 64)) ||
                !bounded(selection.label, 120) ||
                !bounded(selection.textExcerpt, 500) ||
                selection.neighbours.length > 2 ||
                !selection.neighbours.every(value => bounded(value, 200)))
                return { ok: false, reason: 'oversized' };
        }
        const size = sizeIssue(data);
        if (size)
            return { ok: false, reason: size };
        return { ok: true, message: data };
    };
    const parsePresence = (data, expected) => {
        const envelope = envelopeIssue(data, expected, 'streamlain:preview-presence', 'actors');
        if (envelope)
            return { ok: false, reason: envelope };
        const actors = data.actors;
        if (!Array.isArray(actors))
            return { ok: false, reason: 'invalid' };
        if (actors.length > 16)
            return { ok: false, reason: 'oversized' };
        let selfCount = 0;
        const seenKeys = new Set();
        for (const actor of actors) {
            if (!record(actor) ||
                !keys(actor, [
                    'key',
                    'name',
                    'color',
                    'kind',
                    'status',
                    'avatarUrl',
                    'sourceKey',
                    'rowId',
                    'columnId',
                ]) ||
                !bounded(actor.key, 256, true) ||
                typeof actor.name !== 'string' ||
                typeof actor.color !== 'string' ||
                !/^#[0-9a-f]{6}$/iu.test(actor.color) ||
                !['self', 'person', 'assistant'].includes(actor.kind) ||
                !(actor.status === null || typeof actor.status === 'string') ||
                !validAvatarUrl(actor.avatarUrl) ||
                !bounded(actor.sourceKey, 256, true) ||
                !bounded(actor.rowId, 256, true) ||
                !validColumnId(actor.columnId))
                return { ok: false, reason: 'invalid' };
            if (actor.kind === 'self' && ++selfCount > 1)
                return { ok: false, reason: 'invalid' };
            // One actor stands at one place; a repeated key has no single place.
            if (seenKeys.has(actor.key))
                return { ok: false, reason: 'invalid' };
            seenKeys.add(actor.key);
            if (!bounded(actor.name, 80) ||
                !(actor.status === null || bounded(actor.status, 40)))
                return { ok: false, reason: 'oversized' };
        }
        const size = sizeIssue(data);
        if (size)
            return { ok: false, reason: size };
        return { ok: true, message: data };
    };
    const parseContentPick = (data, expected) => {
        const envelope = envelopeIssue(data, expected, 'streamlain:preview-content-pick', 'target');
        if (envelope)
            return { ok: false, reason: envelope };
        const target = data.target;
        if (target !== null &&
            (!record(target) ||
                !keys(target, ['sourceKey', 'rowId', 'columnId', 'inline']) ||
                !bounded(target.sourceKey, 256, true) ||
                !bounded(target.rowId, 256, true) ||
                !validColumnId(target.columnId) ||
                typeof target.inline !== 'boolean' ||
                // Only a field can be typed into, never a whole entry.
                (target.inline && target.columnId === null)))
            return { ok: false, reason: 'invalid' };
        const size = sizeIssue(data);
        if (size)
            return { ok: false, reason: size };
        return { ok: true, message: data };
    };
    const validPlace = (value) => bounded(value.sourceKey, 256, true) &&
        bounded(value.rowId, 256, true) &&
        bounded(value.columnId, 256, true);
    const parseContentEdit = (data, expected) => {
        const envelope = envelopeIssue(data, expected, 'streamlain:preview-content-edit', 'target', 'text');
        if (envelope)
            return { ok: false, reason: envelope };
        const { target, text } = data;
        if (!record(target) ||
            !keys(target, ['sourceKey', 'rowId', 'columnId']) ||
            !validPlace(target) ||
            (text !== undefined && !bounded(text, CONTENT_TEXT_MAX)))
            return { ok: false, reason: 'invalid' };
        const size = sizeIssue(data);
        if (size)
            return { ok: false, reason: size };
        return { ok: true, message: data };
    };
    const parseContentInput = (data, expected) => {
        const envelope = envelopeIssue(data, expected, 'streamlain:preview-content-input', 'input');
        if (envelope)
            return { ok: false, reason: envelope };
        const input = data.input;
        if (!record(input) ||
            !keys(input, 'full' in input
                ? ['sourceKey', 'rowId', 'columnId', 'text', 'done', 'full']
                : ['sourceKey', 'rowId', 'columnId', 'text', 'done']) ||
            ('full' in input && input.full !== true) ||
            !validPlace(input) ||
            !bounded(input.text, CONTENT_TEXT_MAX) ||
            typeof input.done !== 'boolean')
            return { ok: false, reason: 'invalid' };
        const size = sizeIssue(data);
        if (size)
            return { ok: false, reason: size };
        return {
            ok: true,
            message: data,
        };
    };
    const parseContentPlaces = (data, expected) => {
        const envelope = envelopeIssue(data, expected, 'streamlain:preview-content-places', 'places');
        if (envelope)
            return { ok: false, reason: envelope };
        const places = data.places;
        if (!Array.isArray(places) ||
            places.length > 64 ||
            !places.every(place => record(place) &&
                keys(place, ['sourceKey', 'rowId', 'columnId']) &&
                validPlace(place)))
            return { ok: false, reason: 'invalid' };
        const size = sizeIssue(data);
        if (size)
            return { ok: false, reason: size };
        return {
            ok: true,
            message: data,
        };
    };
    const validPoint = (value) => Array.isArray(value.path) &&
        value.path.length > 0 &&
        value.path.length <= 48 &&
        value.path.every(step => Number.isSafeInteger(step) && step >= 0 && step < 4096) &&
        typeof value.tag === 'string' &&
        /^[a-z][a-z0-9-]{0,31}$/u.test(value.tag);
    const parsePoint = (data, expected) => {
        const envelope = envelopeIssue(data, expected, 'streamlain:preview-point', 'point');
        if (envelope)
            return { ok: false, reason: envelope };
        const point = data.point;
        if (point !== null &&
            (!record(point) || !keys(point, ['path', 'tag']) || !validPoint(point)))
            return { ok: false, reason: 'invalid' };
        return { ok: true, message: data };
    };
    const parsePointers = (data, expected) => {
        const envelope = envelopeIssue(data, expected, 'streamlain:preview-pointers', 'pointers');
        if (envelope)
            return { ok: false, reason: envelope };
        const pointers = data.pointers;
        if (!Array.isArray(pointers))
            return { ok: false, reason: 'invalid' };
        if (pointers.length > 16)
            return { ok: false, reason: 'oversized' };
        let selfCount = 0;
        const seenKeys = new Set();
        for (const pointer of pointers) {
            if (!record(pointer) ||
                !keys(pointer, [
                    'key',
                    'name',
                    'color',
                    'kind',
                    'status',
                    'avatarUrl',
                    'path',
                    'tag',
                ]) ||
                !bounded(pointer.key, 256, true) ||
                typeof pointer.name !== 'string' ||
                typeof pointer.color !== 'string' ||
                !/^#[0-9a-f]{6}$/iu.test(pointer.color) ||
                !['self', 'person'].includes(pointer.kind) ||
                !(pointer.status === null || typeof pointer.status === 'string') ||
                !validAvatarUrl(pointer.avatarUrl) ||
                !validPoint(pointer))
                return { ok: false, reason: 'invalid' };
            if (pointer.kind === 'self' && ++selfCount > 1)
                return { ok: false, reason: 'invalid' };
            if (seenKeys.has(pointer.key))
                return { ok: false, reason: 'invalid' };
            seenKeys.add(pointer.key);
            if (!bounded(pointer.name, 80) ||
                !(pointer.status === null || bounded(pointer.status, 40)))
                return { ok: false, reason: 'oversized' };
        }
        const size = sizeIssue(data);
        if (size)
            return { ok: false, reason: size };
        return { ok: true, message: data };
    };
    const readBootstrap = (raw, expected) => {
        try {
            const url = new URL(raw);
            const scope = JSON.parse(url.searchParams.get('__slPreviewSelectionScope') ?? 'null');
            const parentOrigin = url.searchParams.get('__slPreviewSelectionParentOrigin');
            if (!validScope(scope) || !validOrigin(parentOrigin))
                return { ok: false, reason: 'invalid' };
            if (scope.workspaceId !== expected.workspaceId ||
                scope.appBaseId !== expected.appBaseId ||
                scope.artifactVersion !== expected.artifactVersion ||
                scope.basePath !== expected.basePath)
                return { ok: false, reason: 'stale' };
            const suffix = routePath(url.pathname.slice(scope.basePath.length));
            if (!url.pathname.startsWith(scope.basePath + '/') &&
                url.pathname !== scope.basePath)
                return { ok: false, reason: 'stale' };
            if (suffix !== scope.routePath)
                return { ok: false, reason: 'stale' };
            return { ok: true, bootstrap: { schemaVersion: 1, scope, parentOrigin } };
        }
        catch {
            return { ok: false, reason: 'invalid' };
        }
    };
    const createUrl = (raw, bootstrap) => {
        if (bootstrap.schemaVersion !== 1 ||
            !validScope(bootstrap.scope) ||
            !validOrigin(bootstrap.parentOrigin))
            throw new Error('Invalid preview selection bootstrap');
        const url = new URL(raw, bootstrap.parentOrigin);
        url.searchParams.set('__slPreviewSelectionScope', JSON.stringify(bootstrap.scope));
        url.searchParams.set('__slPreviewSelectionParentOrigin', bootstrap.parentOrigin);
        if (!readBootstrap(url.toString(), bootstrap.scope).ok)
            throw new Error('Preview URL scope mismatch');
        return url.toString();
    };
    /** Both host switches have one shape; only the type tells them apart. */
    const switchCommand = (data, expected, type) => record(data) &&
        keys(data, ['schemaVersion', 'type', 'scope', 'enabled']) &&
        data.schemaVersion === 1 &&
        data.type === type &&
        typeof data.enabled === 'boolean' &&
        validScope(data.scope) &&
        matchesScope(data.scope, expected);
    const parsePickingCommand = (data, expected) => switchCommand(data, expected, 'streamlain:preview-picking');
    const parseContentModeCommand = (data, expected) => switchCommand(data, expected, 'streamlain:preview-content-mode');
    const parsePointModeCommand = (data, expected) => switchCommand(data, expected, 'streamlain:preview-point-mode');
    const parseContentLabelsCommand = (data, expected) => record(data) &&
        keys(data, ['schemaVersion', 'type', 'scope', 'labels']) &&
        data.schemaVersion === 1 &&
        data.type === 'streamlain:preview-content-labels' &&
        record(data.labels) &&
        keys(data.labels, ['self', 'ai', 'open', 'hintColor', 'hintIconColor']) &&
        bounded(data.labels.self, 40, true) &&
        bounded(data.labels.ai, 40, true) &&
        bounded(data.labels.open, 40, true) &&
        typeof data.labels.hintColor === 'string' &&
        /^#[0-9a-f]{6}$/iu.test(data.labels.hintColor) &&
        typeof data.labels.hintIconColor === 'string' &&
        /^#[0-9a-f]{6}$/iu.test(data.labels.hintIconColor) &&
        validScope(data.scope) &&
        matchesScope(data.scope, expected);
    const parseSourceMarksCommand = (data, expected) => record(data) &&
        keys(data, ['schemaVersion', 'type', 'scope', 'marks', 'focus']) &&
        data.schemaVersion === 1 &&
        data.type === 'streamlain:preview-source-marks' &&
        (data.focus === null || bounded(data.focus, 128, true)) &&
        Array.isArray(data.marks) &&
        // Equal to `PREVIEW_SOURCE_MARKS_MAX`, named again for the frame.
        data.marks.length <= 32 &&
        data.marks.every(mark => record(mark) &&
            keys(mark, ['sourceKey', 'label', 'color']) &&
            bounded(mark.sourceKey, 128, true) &&
            bounded(mark.label, 80) &&
            typeof mark.color === 'string' &&
            /^#[0-9a-f]{6}$/iu.test(mark.color)) &&
        validScope(data.scope) &&
        matchesScope(data.scope, expected);
    return {
        parsePoint,
        parsePointers,
        parseContentLabelsCommand,
        parseSourceMarksCommand,
        parseMessage,
        parsePresence,
        parseContentPick,
        parseContentEdit,
        parseContentInput,
        parseContentPlaces,
        readBootstrap,
        createUrl,
        matchesScope,
        parsePickingCommand,
        parseContentModeCommand,
        parsePointModeCommand,
    };
}
const previewSelectionCodec = createPreviewSelectionCodec(exports.canonicalPreviewRoutePath);
function parsePreviewSelectionMessage(data, expected) {
    return previewSelectionCodec.parseMessage(data, expected);
}
function parsePreviewPresenceMessage(data, expected) {
    return previewSelectionCodec.parsePresence(data, expected);
}
function parsePreviewContentPickMessage(data, expected) {
    return previewSelectionCodec.parseContentPick(data, expected);
}
function parsePreviewContentEditCommand(data, expected) {
    return previewSelectionCodec.parseContentEdit(data, expected);
}
function parsePreviewContentInputMessage(data, expected) {
    return previewSelectionCodec.parseContentInput(data, expected);
}
function parsePreviewContentPlacesMessage(data, expected) {
    return previewSelectionCodec.parseContentPlaces(data, expected);
}
function parsePreviewPointMessage(data, expected) {
    return previewSelectionCodec.parsePoint(data, expected);
}
function parsePreviewPointersMessage(data, expected) {
    return previewSelectionCodec.parsePointers(data, expected);
}
function createPreviewSelectionUrl(url, bootstrap) {
    return previewSelectionCodec.createUrl(url, bootstrap);
}
function readPreviewSelectionBootstrap(url, expected) {
    return previewSelectionCodec.readBootstrap(url, expected);
}
/** Installed only for admitted author previews. No DOM access outside this window. */
function installAppBasePreviewSelectionRuntime(win, target, codec) {
    const initial = target
        ? codec.readBootstrap(win.location.href, target)
        : null;
    let active = initial?.ok === true;
    let enabled = false;
    let sequence = 0;
    // The element picker and the presence layer both follow these changes.
    const modeListeners = [];
    const presenceListeners = [];
    const contentModeListeners = [];
    let actors = [];
    let presenceSequence = 0;
    let contentMode = false;
    const pointModeListeners = [];
    let pointMode = false;
    let contentPickSequence = 0;
    const contentEditListeners = [];
    let contentEditSequence = 0;
    let contentInputSequence = 0;
    let contentPlacesSequence = 0;
    const contentLabelsListeners = [];
    let contentLabels = null;
    const sourceMarksListeners = [];
    let sourceMarks = [];
    let sourceFocus = null;
    const pointersListeners = [];
    let pointers = [];
    let pointersSequence = 0;
    let pointSequence = 0;
    let bootstrap = initial?.ok ? initial.bootstrap : null;
    // Last published selection; only a pick or a selection is ever withdrawn.
    let selected = false;
    // After a page change in place the Studio follows the shown page; until it
    // speaks under that page, the places of the page wait for it.
    let followed = true;
    let heldPlaces = null;
    const addressed = () => {
        if (!active || !target || !bootstrap)
            return null;
        const read = codec.readBootstrap(win.location.href, target);
        if (!read.ok || read.bootstrap.parentOrigin !== bootstrap.parentOrigin)
            return null;
        return read.bootstrap;
    };
    // Between the address of the next page and its showing the frame names no
    // page: it neither speaks nor hears.
    const current = () => {
        const read = addressed();
        return (read !== null &&
            bootstrap !== null &&
            codec.matchesScope(read.scope, bootstrap.scope));
    };
    /**
     * A page change in place keeps this document: the address names another
     * route of the same frame, everything else of the scope stays. Called once
     * the Studio has been told the shown page, so nothing reaches it under a
     * page it does not know yet.
     */
    const showRoute = () => {
        const read = addressed();
        if (!read || !bootstrap)
            return false;
        if (codec.matchesScope(read.scope, bootstrap.scope))
            return true;
        if (!codec.matchesScope({ ...read.scope, routePath: bootstrap.scope.routePath }, bootstrap.scope))
            return false;
        bootstrap = read;
        // The Studio talks to the new page afresh and counts from one again, as
        // it does for a new document.
        presenceSequence = 0;
        pointersSequence = 0;
        contentEditSequence = 0;
        followed = false;
        return true;
    };
    const publish = (selection) => {
        if (!current() || !bootstrap)
            return;
        const message = {
            schemaVersion: 1,
            type: 'streamlain:preview-selection',
            scope: bootstrap.scope,
            sequence: ++sequence,
            selection,
        };
        if (!codec.parseMessage(message, bootstrap.scope).ok)
            return;
        selected = selection !== null;
        win.parent.postMessage(message, bootstrap.parentOrigin);
    };
    const setMode = (next) => {
        enabled = next;
        for (const listener of modeListeners)
            listener(next);
    };
    const setContentMode = (next) => {
        contentMode = next;
        for (const listener of contentModeListeners)
            listener(next);
    };
    const setPointMode = (next) => {
        if (next === pointMode)
            return;
        pointMode = next;
        for (const listener of pointModeListeners)
            listener(next);
    };
    const setPresence = (next) => {
        actors = next;
        for (const listener of presenceListeners)
            listener(next);
    };
    /** Sent only for a click on content that has a place; never withdrawn. */
    const publishContentPick = (pick) => {
        if (!current() || !bootstrap)
            return;
        const message = {
            schemaVersion: 1,
            type: 'streamlain:preview-content-pick',
            scope: bootstrap.scope,
            sequence: contentPickSequence + 1,
            target: pick,
        };
        if (!codec.parseContentPick(message, bootstrap.scope).ok)
            return;
        contentPickSequence = message.sequence;
        win.parent.postMessage(message, bootstrap.parentOrigin);
    };
    /** At most `PREVIEW_CONTENT_TEXT_MAX` characters; the place itself takes no more. */
    const publishContentInput = (input) => {
        if (!current() || !bootstrap)
            return;
        const message = {
            schemaVersion: 1,
            type: 'streamlain:preview-content-input',
            scope: bootstrap.scope,
            sequence: contentInputSequence + 1,
            input,
        };
        if (!codec.parseContentInput(message, bootstrap.scope).ok)
            return;
        contentInputSequence = message.sequence;
        win.parent.postMessage(message, bootstrap.parentOrigin);
    };
    const setContentLabels = (next) => {
        contentLabels = next;
        for (const listener of contentLabelsListeners)
            listener();
    };
    const setSourceMarks = (next, focus) => {
        sourceMarks = next;
        sourceFocus = focus;
        for (const listener of sourceMarksListeners)
            listener();
    };
    const setPointers = (next) => {
        pointers = next;
        for (const listener of pointersListeners)
            listener();
    };
    /** The element the author points at for the others; `null` takes it off. */
    const publishPoint = (point) => {
        if (!current() || !bootstrap)
            return;
        const message = {
            schemaVersion: 1,
            type: 'streamlain:preview-point',
            scope: bootstrap.scope,
            sequence: pointSequence + 1,
            point,
        };
        if (!codec.parsePoint(message, bootstrap.scope).ok)
            return;
        pointSequence = message.sequence;
        win.parent.postMessage(message, bootstrap.parentOrigin);
    };
    /** At most 64 places; a page that shows more names the first ones. */
    const publishContentPlaces = (places) => {
        if (!current() || !bootstrap)
            return;
        if (!followed) {
            heldPlaces = places;
            return;
        }
        heldPlaces = null;
        const message = {
            schemaVersion: 1,
            type: 'streamlain:preview-content-places',
            scope: bootstrap.scope,
            sequence: contentPlacesSequence + 1,
            places,
        };
        if (!codec.parseContentPlaces(message, bootstrap.scope).ok)
            return;
        contentPlacesSequence = message.sequence;
        win.parent.postMessage(message, bootstrap.parentOrigin);
    };
    /** `true` when the Studio spoke under the shown page. */
    const hear = (data, scope) => {
        if (codec.parsePickingCommand(data, scope)) {
            setMode(data.enabled);
            if (!enabled)
                publish(null);
            return true;
        }
        if (codec.parseContentModeCommand(data, scope)) {
            setContentMode(data.enabled);
            return true;
        }
        if (codec.parsePointModeCommand(data, scope)) {
            setPointMode(data.enabled);
            return true;
        }
        if (codec.parseContentLabelsCommand(data, scope)) {
            setContentLabels(data.labels);
            return true;
        }
        if (codec.parseSourceMarksCommand(data, scope)) {
            setSourceMarks(data.marks, data.focus);
            return true;
        }
        const pointed = codec.parsePointers(data, scope);
        if (pointed.ok) {
            // Always the whole list; an overtaken one must not win.
            if (pointed.message.sequence <= pointersSequence)
                return true;
            pointersSequence = pointed.message.sequence;
            setPointers(pointed.message.pointers);
            return true;
        }
        const edit = codec.parseContentEdit(data, scope);
        if (edit.ok) {
            if (!contentMode || edit.message.sequence <= contentEditSequence)
                return true;
            contentEditSequence = edit.message.sequence;
            for (const listener of contentEditListeners)
                listener(edit.message.target, edit.message.text);
            return true;
        }
        const presence = codec.parsePresence(data, scope);
        if (!presence.ok)
            return false;
        // The host always sends the whole list; an overtaken one must not win.
        if (presence.message.sequence <= presenceSequence)
            return true;
        presenceSequence = presence.message.sequence;
        setPresence(presence.message.actors);
        return true;
    };
    const receive = (event) => {
        if (!current() ||
            !bootstrap ||
            event.source !== win.parent ||
            event.origin !== bootstrap.parentOrigin)
            return;
        if (!hear(event.data, bootstrap.scope) || followed)
            return;
        followed = true;
        const places = heldPlaces;
        heldPlaces = null;
        if (places)
            publishContentPlaces(places);
    };
    const dispose = () => {
        if (!active)
            return;
        publish(null);
        setMode(false);
        setContentMode(false);
        setPointMode(false);
        setPresence([]);
        setContentLabels(null);
        setSourceMarks([], null);
        setPointers([]);
        active = false;
        win.removeEventListener('message', receive);
        win.removeEventListener('pagehide', dispose);
    };
    if (active) {
        win.addEventListener('message', receive);
        win.addEventListener('pagehide', dispose);
    }
    return {
        publish,
        dispose,
        showRoute,
        isPicking: () => enabled && current(),
        onModeChanged: (listener) => {
            modeListeners.push(listener);
        },
        clear: () => {
            const withdrawn = enabled || selected;
            setMode(false);
            if (withdrawn)
                publish(null);
        },
        presence: () => (current() ? actors : []),
        onPresenceChanged: (listener) => {
            presenceListeners.push(listener);
        },
        isContentMode: () => contentMode && current(),
        onContentModeChanged: (listener) => {
            contentModeListeners.push(listener);
        },
        isPointMode: () => pointMode && current(),
        onPointModeChanged: (listener) => {
            pointModeListeners.push(listener);
        },
        publishContentPick,
        onContentEdit: (listener) => {
            contentEditListeners.push(listener);
        },
        publishContentInput,
        publishContentPlaces,
        contentLabels: () => (current() ? contentLabels : null),
        onContentLabelsChanged: (listener) => {
            contentLabelsListeners.push(listener);
        },
        sourceMarks: () => (current() ? sourceMarks : []),
        sourceFocus: () => (current() ? sourceFocus : null),
        onSourceMarksChanged: (listener) => {
            sourceMarksListeners.push(listener);
        },
        pointers: () => (current() ? pointers : []),
        onPointersChanged: (listener) => {
            pointersListeners.push(listener);
        },
        publishPoint,
    };
}
